import { Brand } from '@constants/brands';
import { BaseAPI } from './_api';
import {
  ProductAndCoveragesResponse,
  ProductAvailabilityByLocationRequest,
  ProductAvailabilityByLocationResponse,
  ProductAvailabilityRequest,
  ProductAvailabilityResponse,
  ProductFilters,
  ProductFiltersRequest,
  ProductPricingDetailsRequest,
  ProductPricingDetailsResponse,
  ProductPricingRequest,
  ProductPricingResponse,
  CreateQuoteResponse,
  ProductPricingDetailsSaveQuoteRequest,
  PromoValidationRequest,
  VoucherCodeValidationResponse,
} from './models';
import { coverageNameOverridesDictionary, coverageNameOverridesForHBW210Dictionary } from '@constants/dictionaries';
import { getBrand } from '@helpers/brand.utils';

export class API extends BaseAPI {
  constructor(suppressErrors = false) {
    super(
      'app-ms',
      true,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
      true,
      suppressErrors,
    );
  }

  /** Healthcheck */
  public async healthcheck(): Promise<any> {
    return this.axios.get(`${this.getHost()}/healthcheck`);
  }

  /** From an order, get the filters for the plans and coverage. */
  public async getFilters(body: ProductFiltersRequest): Promise<ProductFilters> {
    return this.axios.post<any, ProductFilters>(`${this.getHost()}/product/filters`, body);
  }

  /** determine product availability generalized for a location to determine if there are products available */
  public async getProductAvailabilityByLocation(
    req: ProductAvailabilityByLocationRequest,
  ): Promise<ProductAvailabilityByLocationResponse> {
    return this.axios.get<any, ProductAvailabilityByLocationResponse>(
      `${this.getHost()}/products/availability`,
      {
        params: req,
      },
    );
  }

  /** retrieve available products offered for the request */
  public async getProductAvailability(
    body: ProductAvailabilityRequest,
  ): Promise<ProductAvailabilityResponse> {
    return this.axios.post<any, ProductAvailabilityResponse>(
      `${this.getHost()}/products/availability`,
      body,
    );
  }

  /** get the product and coverage information for a given product id */
  public async getProductCoveragesByProductId(
    id: string,
    residenceType: string,
  ): Promise<ProductAndCoveragesResponse> {
    const params = {
      residenceType,
    };
    return this.axios
      .get<any, ProductAndCoveragesResponse>(`${this.getHost()}/products/${id}`, { params })
      .then((res) => {
        res.coverages.included = res.coverages.included.map((cvg) => {
          cvg.name = coverageNameOverridesDictionary[cvg.name] || cvg.name;

          if(getBrand() === Brand.HBW210) {
            cvg.name = coverageNameOverridesForHBW210Dictionary[cvg.name?.toUpperCase()] || cvg.name;
          }
          return cvg;
        });
        res.coverages.optional = res.coverages.optional.map((cvg) => {
          cvg.name = coverageNameOverridesDictionary[cvg.name] || cvg.name;

          if(getBrand() === Brand.HBW210) {
            cvg.name = coverageNameOverridesForHBW210Dictionary[cvg.name?.toUpperCase()] || cvg.name;
          }
          return cvg;
        });

        return res;
      });
  }

  /** get product and coverage initial pricing information for given product id */
  public async getProductPricingByProductId(
    id: string,
    body: ProductPricingRequest,
  ): Promise<ProductPricingResponse> {
    return this.axios.post<any, ProductPricingResponse>(
      `${this.getHost()}/products/${id}/pricing`,
      body,
    );
  }

  /** get product pricing details, returning the price summary and the individual coverage prices (for quantity/tier calculations) */
  public async getProductPricingDetails(
    body: ProductPricingDetailsRequest,
  ): Promise<ProductPricingDetailsResponse> {
    return this.axios.post<any, ProductPricingDetailsResponse>(
      `${this.getHost()}/products/pricing`,
      body,
    );
  }

  /** createQuote returning the quoteID */
  public async createQuote(body: ProductPricingDetailsRequest): Promise<CreateQuoteResponse> {
    return this.axios.post<any, CreateQuoteResponse>(`${this.getHost()}/products/quote`, body);
  }

  /** createQuote returning the quoteID & orderId */
  public async saveQuote(
    body: ProductPricingDetailsSaveQuoteRequest,
  ): Promise<CreateQuoteResponse> {
    return this.axios.post<any, CreateQuoteResponse>(
      `${this.getHost()}/plans-and-prices/order`,
      body,
    );
  }

  /** validate promo codes*/
  public async validatePromos(
    body: PromoValidationRequest,
  ): Promise<VoucherCodeValidationResponse> {
    return this.axios.post<any, VoucherCodeValidationResponse>(
      `${this.getHost()}/products/promos/validate`,
      body,
    );
  }
}

const ProductApi = new API();
export const ProductApiSuppressErrors = new API(true);
export default ProductApi;
